import React from 'react';

import PropTypes from 'prop-types';

import { useTheme, useComponentIcons } from '@adsk/alloy-react-styles';
import type { NotificationToastDesignTokens } from '@adsk/alloy-react-styles';
import { stylePropType, StylableComponent } from '@adsk/alloy-react-theme';

import { NOTIFICATION_STATUSES, NotificationStatus } from './helpers/consts';

type NotificationIconProps = StylableComponent<HTMLDivElement> & {
  status?: NotificationStatus;
};

/** Maps a non-`none` status to its icon key in the component icon registry. */
const STATUS_ICON_KEY = {
  [NOTIFICATION_STATUSES.INFO]: 'infoIcon',
  [NOTIFICATION_STATUSES.SUCCESS]: 'successIcon',
  [NOTIFICATION_STATUSES.WARNING]: 'warningIcon',
  [NOTIFICATION_STATUSES.ERROR]: 'errorIcon',
} as const;

const NotificationIcon = React.forwardRef<
  HTMLDivElement,
  NotificationIconProps
>(
  (
    { style, className, status = NOTIFICATION_STATUSES.INFO, ...props },
    ref,
  ) => {
    const themeTokens = useTheme();
    const notifTokens = (themeTokens.tokens?.component?.notificationToast ||
      themeTokens.tokens?.component?.notificationtoast) as
      | NotificationToastDesignTokens
      | undefined;
    const iconFill = notifTokens?.[status]?.icon?.fill;
    const icons = useComponentIcons('notification');

    if (status === NOTIFICATION_STATUSES.NONE) {
      return null;
    }

    const StatusIcon = icons[STATUS_ICON_KEY[status]];

    return (
      <div
        {...props}
        ref={ref}
        className={className}
        css={[
          {
            width: 24,
            height: 24,
            color: iconFill,
          },
          style,
        ]}
      >
        <StatusIcon />
      </div>
    );
  },
);

NotificationIcon.displayName = 'Notifications';

NotificationIcon.propTypes = {
  /** Styles applied to the root element */
  style: stylePropType,
  /** Class applied to the root element */
  className: PropTypes.string,
  /** Notification status */
  status: PropTypes.oneOf(Object.values(NOTIFICATION_STATUSES)),
};

export default NotificationIcon;
